import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { useClientPortalStore } from './stores/clientPortalStore';
import LoginPage from './pages/LoginPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import PatientsPage from './pages/PatientsPage';
import PatientDetailPage from './pages/PatientDetailPage';
import AppointmentsPage from './pages/AppointmentsPage';
import MedicalRecordsPage from './pages/MedicalRecordsPage';
import MedicalRecordDetailPage from './pages/MedicalRecordDetailPage';
import InvoicingPage from './pages/InvoicingPage';
import QueuePage from './pages/QueuePage';
import AdminPage from './pages/AdminPage';
import ReportsPage from './pages/ReportsPage';
import PrescriptionsPage from './pages/PrescriptionsPage';
import InventoryPage from './pages/InventoryPage';
import BoardingPage from './pages/BoardingPage';
import RemindersPage from './pages/RemindersPage';
import LabResultsPage from './pages/LabResultsPage';
import SettingsPage from './pages/SettingsPage';
import VaccinationsPage from './pages/VaccinationsPage';
import ControlledDrugsPage from './pages/ControlledDrugsPage';
import SurgeriesPage from './pages/SurgeriesPage';
import RecallCampaignsPage from './pages/RecallCampaignsPage';
import CustomReportPage from './pages/CustomReportPage';
import PaymentPlansPage from './pages/PaymentPlansPage';
import NotificationsPage from './pages/NotificationsPage';
import AIAssistPage from './pages/AIAssistPage';
import AccountingExportPage from './pages/AccountingExportPage';
import RosterPage from './pages/RosterPage';
import LogsPage from './pages/LogsPage';
import ServicesPage from './pages/ServicesPage';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import Layout from './components/Layout';
import ClientLoginPage from './pages/ClientLoginPage';
import ClientPortalLayout from './components/ClientPortalLayout';
import PortalDashboardPage from './pages/portal/PortalDashboardPage';
import PortalPetsPage from './pages/portal/PortalPetsPage';
import PortalAppointmentsPage from './pages/portal/PortalAppointmentsPage';
import PortalInvoicesPage from './pages/portal/PortalInvoicesPage';
import PortalMedicalPage from './pages/portal/PortalMedicalPage';
import PortalRefillsPage from './pages/portal/PortalRefillsPage';

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const PortalRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useClientPortalStore();
  return isAuthenticated ? <>{children}</> : <Navigate to="/client-portal/login" replace />;
};

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />

        {/* Client portal */}
        <Route path="/client-portal/login" element={<ClientLoginPage />} />
        <Route
          path="/client-portal/*"
          element={
            <PortalRoute>
              <ClientPortalLayout>
                <Routes>
                  <Route path="/" element={<PortalDashboardPage />} />
                  <Route path="/pets" element={<PortalPetsPage />} />
                  <Route path="/appointments" element={<PortalAppointmentsPage />} />
                  <Route path="/invoices" element={<PortalInvoicesPage />} />
                  <Route path="/medical" element={<PortalMedicalPage />} />
                  <Route path="/refills" element={<PortalRefillsPage />} />
                </Routes>
              </ClientPortalLayout>
            </PortalRoute>
          }
        />

        {/* Staff app */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/clients" element={<ClientsPage />} />
                  <Route path="/clients/:id" element={<ClientDetailPage />} />
                  <Route path="/patients" element={<PatientsPage />} />
                  <Route path="/patients/:id" element={<PatientDetailPage />} />
                  <Route path="/appointments" element={<AppointmentsPage />} />
                  <Route path="/medical-records" element={<MedicalRecordsPage />} />
                  <Route path="/medical-records/:id" element={<MedicalRecordDetailPage />} />
                  <Route path="/prescriptions" element={<PrescriptionsPage />} />
                  <Route path="/invoicing" element={<InvoicingPage />} />
                  <Route path="/queue" element={<QueuePage />} />
                  <Route path="/inventory" element={<InventoryPage />} />
                  <Route path="/lab-results" element={<LabResultsPage />} />
                  <Route path="/boarding" element={<BoardingPage />} />
                  <Route path="/reminders" element={<RemindersPage />} />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/vaccinations" element={<VaccinationsPage />} />
                  <Route path="/controlled-drugs" element={<ControlledDrugsPage />} />
                  <Route path="/surgeries" element={<SurgeriesPage />} />
                  <Route path="/recall-campaigns" element={<RecallCampaignsPage />} />
                  <Route path="/custom-reports" element={<CustomReportPage />} />
                  <Route path="/payment-plans" element={<PaymentPlansPage />} />
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/ai-assist" element={<AIAssistPage />} />
                  <Route path="/accounting" element={<AccountingExportPage />} />
                  <Route path="/roster" element={<RosterPage />} />
                  <Route path="/logs" element={<LogsPage />} />
                  <Route path="/services" element={<ServicesPage />} />
                  <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
